import * as _ from "lodash";
import React, { useState } from "react";
import { Form } from "react-bootstrap";
import Multiselect from "react-multi-select-component";
import PhysicalLocation, { getPhysicalLocationDisplayName } from "domain/physicalLocation";
import Commodity from "domain/commodity";
import PageFilter from "./pageFilter";
import './PageFilterPanel.scss';
import { getTraderDisplayName, Trader } from "features/settings/traders/trader";
import { DrawerState } from "common/ui/useDrawer";
import { waitForKeystrokes } from "common/ui/waitForKeystrokes";
import { useSelector } from "react-redux";
import IAppStore from "app/IAppStore";
import { localizeFilterPanel as localize, localizeCommon } from "features/localization/localizeService";
import useTraders from "features/settings/traders/useTraders";

export type PageFilterPanelProps = {
    locations: PhysicalLocation[];
    filter: PageFilter;
    showTraders?: boolean;
    showSearch?: boolean;
    applyFilter(filterParams: PageFilter): void;
    drawerState: DrawerState;
};

interface Option {
    value: any;
    label: string;
}

interface OptionWithData<T> extends Option {
    data: T
};

const SellValue = "Sell";
const BuyValue = "Buy";
const BuySellValue = "Buy/Sell";

const PageFilterPanel: React.FC<PageFilterPanelProps> = props => {
    return props.drawerState.isShowing
        ? <PageFilterPanelDetails {...props} />
        : <></>;
}

const PageFilterPanelDetails: React.FC<PageFilterPanelProps> = props => {

    const [searchText, setSearchText] = useState("");
    const isCustomer: boolean = useSelector((m: IAppStore) => m.userState.isCustomer);

    const myCommodities = useSelector((m: IAppStore) => m.myCommodities);
    const traders = useTraders();

    const commodities = _.orderBy(
        _.intersectionBy(
            props.locations.flatMap(l => l.commodities),
            myCommodities,
            m => m.id),
        c => c.name);

    function reset() {
        props.applyFilter(new PageFilter());
    }

    const onSearchChanged = waitForKeystrokes((search: string) => {
        props.applyFilter({ ...props.filter, search })
        setSearchText(search);
    });

    function getLocationList(): PhysicalLocation[] {
        const homeLocations = _.orderBy(
            props.locations.filter(m => m.homeLocation == null),
            l => l.name);

        return homeLocations.flatMap(hl => [
            hl,
            ..._.orderBy(props.locations.filter(l => l.homeLocation?.id === hl.id),
                l => l.name)
        ]);
    }



    function selectFilterOptions<T>(selected: OptionWithData<T>[], setter: ((filter: PageFilter, val: T[]) => void)) {
        const filter = { ...props.filter };
        setter(filter, selected.map(m => m.data));

        props.applyFilter(filter);
    }


    function getWillSellValue(): string {
        if (props.filter.willSell == null) return BuySellValue;

        return props.filter.willSell
            ? SellValue
            : BuyValue
    }

    function filterOptions(options: Option[], filter: string): Option[] {
        if (!filter) {
            return options;
        }
        return options.filter(({ label }) => label && label.toLowerCase().includes(filter.toLowerCase()));
    };

    function selectWillSell(optionValue: string) {
        const filter = { ...props.filter };
        switch (optionValue) {
            case SellValue:
                filter.willSell = true;
                break;
            case BuyValue:
                filter.willSell = false;
                break;
            default:
                filter.willSell = null;
                break;
        }

        props.applyFilter(filter);
    }

    function nestedItemRenderer({
        checked,
        option,
        onClick,
        disabled,
    }: any) {
        return (
            <div
                className={`multiselect-item item-renderer ${option.data.homeLocation == null ? '' : 'nested'}`}
            >
                <input
                    type="checkbox"
                    onChange={onClick}
                    checked={checked}
                    tabIndex={-1}
                    disabled={disabled}
                />
                <span className="hovertext" data-hover={option.label}>{option.label}</span>
            </div>
        );
    };

    function isLoaded(): boolean {
        return (!props.showTraders || traders != null) && myCommodities != null
    }

    if (!isLoaded()) return <></>;

    return <div className="container left-drawer">
        {props.showSearch && <>
            <div className="form-row panel-group">
                <div className="col">
                    <span className="title">{localize("PageFilterPanel_Label_Search")}</span>
                </div>
            </div>
            <div className="form-row search-box">
                <div className="col">
                    <input type="text"
                        className="form-control"
                        autoFocus={props.drawerState.isSearchFocused}
                        defaultValue={searchText}
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                            onSearchChanged(ev.target.value)
                        }}
                    />
                </div>
            </div>
        </>}
        <div className="form-row panel-group justify-content-between filter">
            <div className="col">
                <span className="title">{localize("PageFilterPanel_Label_Filters")}</span>
            </div>
            <div className="col">
                <div className="pull-right">
                    <button className="btn btn-link" onClick={() => reset()}>{localize("PageFilterPanel_Button_Reset")}</button>
                </div>
            </div>
        </div>
        <div className="form-row">
            <Form.Group className="col">
                <Form.Label>{localize("PageFilterPanel_Label_Locations")}</Form.Label>
                <Multiselect
                    options={getLocationList().map(l => getLocationOption(l, isCustomer))}
                    value={props.filter.locations.map(l => getLocationOption(l, isCustomer))}
                    labelledBy="All"
                    filterOptions={filterOptions}
                    className="locations"
                    hasSelectAll={false}
                    valueRenderer={items => getFilterText(items)}
                    ItemRenderer={nestedItemRenderer}
                    onChange={(items: OptionWithData<PhysicalLocation>[]) => selectFilterOptions(
                        items,
                        (filter, locations) => filter.locations = locations)} />
            </Form.Group>
        </div>
        <div className="form-row">
            <Form.Group className="col">
                <Form.Label>{localize("PageFilterPanel_Label_Commodities")}</Form.Label>
                <Multiselect
                    options={commodities.map(c => getCommodityOption(c))}
                    value={props.filter.commodities.map(c => getCommodityOption(c))}
                    labelledBy="All"
                    disableSearch
                    hasSelectAll={false}
                    valueRenderer={items => getFilterText(items)}
                    onChange={(items: OptionWithData<Commodity>[]) => selectFilterOptions(
                        items,
                        (filter, commodities) => filter.commodities = commodities)} />
            </Form.Group>
        </div>
        {props.showTraders && !isCustomer &&
            <div className="form-row">
                <Form.Group className="col">
                    <Form.Label>{localize("PageFilterPanel_Label_Traders")}</Form.Label>
                    <Multiselect
                        options={traders.map(t => getTraderOption(t))}
                        value={props.filter.traders.map(t => getTraderOption(t))}
                        labelledBy="All"
                        disableSearch
                        hasSelectAll={false}
                        valueRenderer={items => getFilterText(items)}
                        onChange={(items: OptionWithData<Trader>[]) => selectFilterOptions(
                            items,
                            (filter, traders) => filter.traders = traders)} />
                </Form.Group>
            </div>
        }
        {!isCustomer &&
            <div className="form-row">
                <Form.Group className="col">
                    <Form.Label>{localize("PageFilterPanel_Label_BuySell")}</Form.Label>
                    <Form.Control as="select"
                        value={getWillSellValue()}
                        onChange={(ev: React.ChangeEvent<HTMLSelectElement>) => selectWillSell(ev.target.value)}>
                        {getBuySellList().map(val =>
                            <option key={val} value={val}>{getBuySellValue(val)}</option>
                        )}
                    </Form.Control>
                </Form.Group>
            </div>
        }

        <div className="form-row">
            <button className="btn btn-link align-material-icon" onClick={() => props.drawerState.collapse()}>
                <i className="material-icons">chevron_left</i>
                {localize("PageFilterPanel_Button_CloseMenu")}
            </button>
        </div>
    </div>
}

function getFilterText(selected: Option[]): string {
    if (selected.length === 0) return localizeCommon("Value_All");

    if (selected.length === 1) return selected[0].label;

    return localize("PageFilterPanel_Value_Selected", {count: selected.length});
}

function getBuySellList(): string[] {
    return [
        SellValue,
        BuyValue,
        BuySellValue
    ];
}

function getBuySellValue(value: string): string {
    switch (value) {
        case SellValue:
            return localizeCommon("CustomerWill_Value_Sell");
        case BuyValue:
            return localizeCommon("CustomerWill_Value_Buy");
        case BuySellValue:
            return localizeCommon("CustomerWill_Value_BuySell");
    }
}

function getLocationOption(item: PhysicalLocation, isCustomer: boolean): OptionWithData<PhysicalLocation> {
    return { value: item.id, label: getPhysicalLocationDisplayName(item, isCustomer), data: item };
}

function getCommodityOption(item: Commodity): OptionWithData<Commodity> {
    return { value: item.id, label: item.name, data: item };
}

function getTraderOption(item: Trader): OptionWithData<Trader> {
    return { value: item.userId, label: getTraderDisplayName(item), data: item };
}

export default PageFilterPanel;